/**
 * Mobile Detection and Optimization Utilities
 * 
 * Lightweight mobile detection and performance optimizations
 * for better mobile experience.
 */

export interface MobileCapabilities {
  isMobile: boolean;
  isLowEnd: boolean;
  isSlowConnection: boolean;
  supportsIntersectionObserver: boolean;
  supportsWebWorkers: boolean;
  memoryLimit: number; // MB
  recommendedBatchSize: number;
  recommendedDelay: number;
}

export const getMobileCapabilities = (): MobileCapabilities => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  
  // Detect low-end devices
  const isLowEnd = isMobile && (
    navigator.hardwareConcurrency <= 2 || // 2 or fewer CPU cores
    (navigator.deviceMemory && navigator.deviceMemory <= 2) || // 2GB or less RAM
    /android.*4\.[0-3]|android.*5\.[0-1]/i.test(userAgent) // Old Android versions
  );
  
  // Detect slow connection
  const connection = (navigator as any).connection;
  const isSlowConnection = connection && (
    connection.effectiveType === 'slow-2g' ||
    connection.effectiveType === '2g' ||
    connection.effectiveType === '3g' ||
    connection.downlink <= 1.5 // 1.5 Mbps or slower
  );
  
  // Feature detection
  const supportsIntersectionObserver = 'IntersectionObserver' in window;
  const supportsWebWorkers = 'Worker' in window;
  
  // Memory limits based on device capabilities
  const memoryLimit = isLowEnd ? 50 : isMobile ? 100 : 200;
  
  // Performance recommendations
  const recommendedBatchSize = isLowEnd ? 2 : isMobile ? 3 : 5;
  const recommendedDelay = isSlowConnection ? 200 : isMobile ? 100 : 50;
  
  return {
    isMobile,
    isLowEnd,
    isSlowConnection,
    supportsIntersectionObserver,
    supportsWebWorkers,
    memoryLimit,
    recommendedBatchSize,
    recommendedDelay
  };
};

export const getMobileLoadingStrategy = (capabilities: MobileCapabilities) => {
  return {
    // Reduce batch sizes for mobile
    batchSize: capabilities.recommendedBatchSize,
    delayBetweenBatches: capabilities.recommendedDelay,
    
    // Reduce animation complexity
    useReducedMotion: capabilities.isLowEnd,
    
    // Optimize for mobile viewport
    viewportMargin: capabilities.isMobile ? '50px' : '100px',
    
    // Memory management
    maxConcurrentLoads: capabilities.isLowEnd ? 2 : capabilities.isMobile ? 3 : 5,
    
    // Preload strategy
    preloadCritical: !capabilities.isSlowConnection,
    preloadCount: capabilities.isLowEnd ? 1 : capabilities.isMobile ? 2 : 3
  };
};

// Mobile-specific optimizations
export const applyMobileOptimizations = () => {
  const capabilities = getMobileCapabilities();
  
  if (capabilities.isMobile) {
    // Reduce motion for low-end devices
    if (capabilities.isLowEnd) {
      document.documentElement.style.setProperty('--animation-duration', '0.1s');
      document.documentElement.style.setProperty('--transition-duration', '0.1s');
    }
    
    // Optimize touch interactions
    document.documentElement.style.setProperty('--touch-action', 'manipulation');
    
    // Reduce memory usage
    if (capabilities.isLowEnd) {
      // Disable expensive features
      document.documentElement.classList.add('low-end-device');
    }
  }
  
  return capabilities;
};
